import type { BaseQueryFn } from "@reduxjs/toolkit/query";
import type { AxiosRequestConfig, AxiosError, AxiosInstance } from "axios";
import {
  MissingRefreshTokenError,
  isInvalidSessionError,
} from "../../shared/auth";

export const axiosBaseQuery =
  (
    axiosInstance: AxiosInstance,
  ): BaseQueryFn<
    {
      url: string;
      method?: AxiosRequestConfig["method"];
      data?: AxiosRequestConfig["data"];
      params?: AxiosRequestConfig["params"];
      headers?: AxiosRequestConfig["headers"];
      signal?: AxiosRequestConfig["signal"];
      timeout?: AxiosRequestConfig["timeout"];
      suppressErrorToast?: AxiosRequestConfig["suppressErrorToast"];
      authRecovery?: AxiosRequestConfig["authRecovery"];
    },
    unknown,
    unknown
  > =>
  async (
    {
      url,
      method,
      data,
      params,
      headers,
      signal,
      timeout,
      suppressErrorToast,
      authRecovery,
    },
    { signal: querySignal },
  ) => {
    try {
      const result = await axiosInstance({
        url,
        method,
        data,
        params,
        headers,
        timeout,
        suppressErrorToast,
        // RTK Query aborts this signal when it tears a query down. Without it
        // an in-flight request outlives its cache entry and keeps holding a
        // connection the kiosk can no longer observe. An explicitly supplied
        // signal still wins so callers can cancel a single request.
        signal: signal ?? querySignal,
        authRecovery,
      });
      return { data: result.data };
    } catch (axiosError) {
      if (
        isInvalidSessionError(axiosError) ||
        axiosError instanceof MissingRefreshTokenError
      ) {
        return {
          error: {
            status: "SESSION_INVALID",
            data: {
              refresh_status: isInvalidSessionError(axiosError)
                ? axiosError.status
                : undefined,
            },
          },
        };
      }
      const err = axiosError as AxiosError & { status?: number };
      return {
        error: {
          // PermissionDeniedError intentionally wraps the Axios response so it
          // cannot be mistaken for a refreshable 401. Preserve its typed
          // status for RTK Query consumers that must stop polling on 403.
          status: err.response?.status ?? err.status,
          data: err.response?.data || err.message,
        },
      };
    }
  };
